import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { User } from 'firebase/auth';

import {
  subscribeAuth,
  signOutUser,
  signInWithEmail,
  registerWithEmail,
  signInAsGuest as signInAsGuestService,
  deleteCurrentAuthUser,
} from '../services/authService';
import {
  ACCOUNT_DELETION_GRACE_DAYS,
  getUserProfile,
  isProfileComplete,
  isAccountPendingDeletion,
  isDeletionGraceExpired,
  requestAccountDeletion,
  cancelAccountDeletion,
  deleteUserDocument,
} from '../services/userService';
import {
  registerPushNotificationsForUser,
  unregisterPushNotificationsForUser,
} from '../services/notificationService';
import type { UserProfile } from '../types/models';

const DELETION_ACK_MESSAGE = `Üyeliğiniz kapatılmıştır. ${ACCOUNT_DELETION_GRACE_DAYS} gün içinde giriş yapmazsanız hesabınız tamamen kaldırılacaktır.`;

type AuthContextValue = {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  profileLoading: boolean;
  /** Firestore'da profil yok veya zorunlu alanlar eksikse true (anonim kullanıcılar hariç) */
  needsOnboarding: boolean;
  /** Soft delete talebi var ve süre dolmamış — ana uygulama yerine kurtarma ekranı */
  accountSuspended: boolean;
  /** Silme sonrası çıkışta gösterilecek bilgilendirme metni (null ise yok) */
  accountDeletionAckMessage: string | null;
  clearAccountDeletionAck: () => void;
  refreshProfile: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  signInAsGuest: () => Promise<void>;
  logout: () => Promise<void>;
  /** Soft delete + çıkış + bilgilendirme metni */
  deleteMyAccount: () => Promise<void>;
  /** Askıyı kaldırır */
  reactivateAccount: () => Promise<void>;
  /** Süresi dolmuş silme talebi — Firestore + Auth temizlenirken */
  expiredAccountPurging: boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [accountDeletionAckMessage, setAccountDeletionAckMessage] = useState<string | null>(null);
  const [expiredAccountPurging, setExpiredAccountPurging] = useState(false);
  const expiredPurgeRanForUid = useRef<string | null>(null);

  const refreshProfile = useCallback(async () => {
    if (!user?.uid || user?.isAnonymous) {
      setProfile(null);
      return;
    }
    setProfileLoading(true);
    try {
      const p = await getUserProfile(user.uid);
      setProfile(p);
    } finally {
      setProfileLoading(false);
    }
  }, [user?.uid, user?.isAnonymous]);

  useEffect(() => {
    const unsub = subscribeAuth(u => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      return;
    }
    if (user.isAnonymous) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    refreshProfile();
  }, [user?.uid, user?.isAnonymous, refreshProfile]);

  const deletionRequestedAtMs =
    profile?.deletionRequestedAt && typeof profile.deletionRequestedAt.toMillis === 'function'
      ? profile.deletionRequestedAt.toMillis()
      : undefined;

  useEffect(() => {
    if (!user?.uid) {
      expiredPurgeRanForUid.current = null;
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || user.isAnonymous || profileLoading || !profile || !deletionRequestedAtMs) {
      return;
    }
    if (!isAccountPendingDeletion(profile)) {
      return;
    }
    if (!isDeletionGraceExpired(profile.deletionRequestedAt!)) {
      return;
    }
    if (expiredPurgeRanForUid.current === user.uid) {
      return;
    }
    expiredPurgeRanForUid.current = user.uid;

    setExpiredAccountPurging(true);
    (async () => {
      try {
        await deleteUserDocument(user.uid);
        await deleteCurrentAuthUser();
      } catch {
        try {
          await signOutUser();
        } catch {
          /* noop */
        }
      } finally {
        setExpiredAccountPurging(false);
      }
    })();
  }, [user?.uid, user?.isAnonymous, profileLoading, profile?.accountStatus, deletionRequestedAtMs]);

  const accountSuspended = Boolean(
    user &&
      !user.isAnonymous &&
      profile &&
      isAccountPendingDeletion(profile) &&
      profile.deletionRequestedAt &&
      !isDeletionGraceExpired(profile.deletionRequestedAt),
  );

  const needsOnboarding = Boolean(
    user &&
      !user.isAnonymous &&
      !accountSuspended &&
      (profile === null || !isProfileComplete(profile)),
  );

  /** Profil tamam, ana ekrana geçişte FCM izni + token Firestore'a */
  useEffect(() => {
    const uid = user?.uid;
    if (!uid || user?.isAnonymous || needsOnboarding || profileLoading || accountSuspended) {
      return;
    }
    void registerPushNotificationsForUser(uid).catch(() => {
      /* izin reddi vb. sessiz */
    });
  }, [user?.uid, user?.isAnonymous, needsOnboarding, profileLoading, accountSuspended]);

  const login = useCallback(async (email: string, password: string) => {
    await signInWithEmail(email, password);
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    await registerWithEmail(email, password);
  }, []);

  const signInAsGuest = useCallback(async () => {
    await signInAsGuestService();
  }, []);

  const clearAccountDeletionAck = useCallback(() => {
    setAccountDeletionAckMessage(null);
  }, []);

  const logout = useCallback(async () => {
    const uid = user?.uid;
    if (uid) {
      try {
        await unregisterPushNotificationsForUser(uid);
      } catch {
        /* ağ hatası: yine de çıkış */
      }
    }
    await signOutUser();
    setProfile(null);
  }, [user?.uid]);

  const deleteMyAccount = useCallback(async () => {
    const uid = user?.uid;
    if (!uid) {
      return;
    }
    await requestAccountDeletion(uid);
    try {
      await unregisterPushNotificationsForUser(uid);
    } catch {
      /* yine de çıkış */
    }
    setAccountDeletionAckMessage(DELETION_ACK_MESSAGE);
    await signOutUser();
    setProfile(null);
  }, [user?.uid]);

  const reactivateAccount = useCallback(async () => {
    const uid = user?.uid;
    if (!uid) {
      return;
    }
    await cancelAccountDeletion(uid);
    await refreshProfile();
  }, [user?.uid, refreshProfile]);

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      profileLoading,
      needsOnboarding,
      accountSuspended,
      accountDeletionAckMessage,
      clearAccountDeletionAck,
      refreshProfile,
      login,
      register,
      signInAsGuest,
      logout,
      deleteMyAccount,
      reactivateAccount,
      expiredAccountPurging,
    }),
    [
      user,
      profile,
      loading,
      profileLoading,
      needsOnboarding,
      accountSuspended,
      accountDeletionAckMessage,
      clearAccountDeletionAck,
      refreshProfile,
      login,
      register,
      signInAsGuest,
      logout,
      deleteMyAccount,
      reactivateAccount,
      expiredAccountPurging,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth AuthProvider dışında kullanılamaz');
  }
  return ctx;
}
